// SW-27: "OPEN IN APP" pill on the readout header, phones only (Owner
// 2026-09-11: the web dashboard opens the app if installed, otherwise leads
// to the download). Desktop UAs get nothing.
import { el } from "../util/dom";

export const ANDROID_PACKAGE = "com.bitclock.android";
export const IOS_APP_ID = "6809080018";
// store URLs replace FALLBACK_URL at public launch (Play:
// https://play.google.com/store/apps/details?id=com.bitclock.android; App
// Store: https://apps.apple.com/app/id6809080018). Neither app is public yet.
export const FALLBACK_URL = "https://bitclock.net/download";
export const IOS_FALLBACK_MS = 1500;

export type Platform = "android" | "ios" | "chromeos" | null;

export function detectPlatform(ua: string, maxTouchPoints = 0): Platform {
  if (/Android/i.test(ua)) return "android";
  // ChromeOS runs the Play app, but Chrome there does not parse intent:// (the
  // Owner's Chromebook tap did nothing, 2026-09-11); it hands a plain custom
  // scheme to the Android runtime instead, so it takes the scheme+timer path.
  if (/CrOS/i.test(ua)) return "chromeos";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  // iPadOS reports a Macintosh UA; a Mac has no touch points.
  if (/Macintosh/i.test(ua) && maxTouchPoints > 1) return "ios";
  return null;
}

// Chrome opens the app when the package is installed, else navigates to the
// fallback URL itself -- no JS timers needed.
export function androidIntentUrl(): string {
  return `intent://open#Intent;scheme=bitclock;package=${ANDROID_PACKAGE};S.browser_fallback_url=${encodeURIComponent(FALLBACK_URL)};end`;
}

type IosWindow = { location: { href: string }; setTimeout(fn: () => void, ms: number): number; clearTimeout(id: number): void };
type IosDocument = { visibilityState: string; addEventListener(type: "visibilitychange", fn: () => void, opts?: { once: boolean }): void };

// iOS (and ChromeOS) have no intent: URL. Fire the custom scheme; if the page is still
// visible when the timer lands, the app did not open -> go to the fallback.
// The timer is also cleared the moment the page goes hidden, because Safari
// suspends timers in the background and would otherwise fire this one (with
// the page visible again) when the user returns from the app.
export function openIosApp(win: IosWindow, doc: IosDocument): void {
  const timer = win.setTimeout(() => {
    if (doc.visibilityState !== "hidden") win.location.href = FALLBACK_URL;
  }, IOS_FALLBACK_MS);
  doc.addEventListener("visibilitychange", () => win.clearTimeout(timer), { once: true });
  win.location.href = "bitclock://open";
}

/** The pill, or null on desktop (nothing is rendered there). */
export function buildOpenInAppPill(
  ua: string = navigator.userAgent,
  maxTouchPoints: number = navigator.maxTouchPoints
): HTMLAnchorElement | null {
  const platform = detectPlatform(ua, maxTouchPoints);
  if (platform === null) return null;
  const pill = el("a", { class: "nav-btn", href: platform === "android" ? androidIntentUrl() : "bitclock://open" }, [
    "OPEN IN APP",
  ]);
  if (platform !== "android") {
    pill.addEventListener("click", (event) => {
      event.preventDefault();
      openIosApp(window, document);
    });
  }
  return pill;
}
